/** @typedef {'floor' | 'wall' | 'both'} FloorPlanTextureCategory */

/**
 * @typedef {{
 *   id: string;
 *   label: string;
 *   source: string;
 *   category: FloorPlanTextureCategory;
 * }} FloorPlanTextureCatalogEntry
 */

const LEGACY_LABELS = {
  'floor-default': 'Default Floor',
  'wall-inside-default': 'Inside Wall',
  'wall-outside-default': 'Outside Wall',
};

const LEGACY_ID_BY_FILENAME = {
  floor_texture: 'floor-default',
  wall_inside_texture: 'wall-inside-default',
  wall_outside_texture: 'wall-outside-default',
};

/**
 * Build-time catalog via webpack require.context (CRA-compatible).
 * @returns {Record<string, string>}
 */
function loadTextureModules() {
  /** @type {Record<string, string>} */
  const modules = {};
  const context = require.context(
    '../assets/design_tools/floorplan_to_image/textures',
    false,
    /\.(png|jpe?g|webp)$/i,
  );
  context.keys().forEach((key) => {
    modules[key] = context(key);
  });
  return modules;
}

/**
 * @param {string} filename
 */
function slugFromFilename(filename) {
  return filename
    .replace(/\.[^.]+$/, '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
}

/**
 * @param {string} slug
 */
function titleFromSlug(slug) {
  return slug
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * @param {string} slug
 * @returns {FloorPlanTextureCategory}
 */
function inferCategory(slug) {
  const normalized = slug.toLowerCase();
  const isFloor = normalized.includes('floor');
  const isWall =
    normalized.includes('wall') ||
    normalized.includes('inside') ||
    normalized.includes('outside');
  if (isFloor && isWall) return 'both';
  if (isFloor) return 'floor';
  if (isWall) return 'wall';
  return 'both';
}

/** @type {Map<string, FloorPlanTextureCatalogEntry>} */
const catalogById = new Map();

for (const [path, source] of Object.entries(loadTextureModules())) {
  if (typeof source !== 'string') continue;
  const filename = path.split('/').pop() || path;
  const slug = slugFromFilename(filename);
  const id = LEGACY_ID_BY_FILENAME[slug] || slug;
  if (catalogById.has(id)) continue;
  catalogById.set(id, {
    id,
    label: LEGACY_LABELS[id] || titleFromSlug(slug),
    source,
    category: inferCategory(slug),
  });
}

/** @type {FloorPlanTextureCatalogEntry[]} */
export const FLOORPLAN_TEXTURE_CATALOG = [...catalogById.values()].sort((a, b) =>
  a.label.localeCompare(b.label),
);

/**
 * @param {string} textureId
 * @returns {'inside' | 'outside' | null}
 */
function inferWallSideFromTextureId(textureId) {
  const id = textureId.toLowerCase();
  if (id.includes('outside')) return 'outside';
  if (id.includes('inside')) return 'inside';
  return null;
}

/**
 * @param {'floor' | 'wall'} category
 * @returns {FloorPlanTextureCatalogEntry[]}
 */
export function getTexturesForCategory(category) {
  if (category === 'floor') {
    return FLOORPLAN_TEXTURE_CATALOG.filter((entry) => {
      const id = entry.id.toLowerCase();
      return id.includes('floor') || entry.category === 'floor';
    });
  }
  return FLOORPLAN_TEXTURE_CATALOG.filter((entry) => {
    const id = entry.id.toLowerCase();
    return !id.includes('floor') && entry.category !== 'floor';
  });
}

/**
 * @param {'inside' | 'outside'} side
 * @returns {FloorPlanTextureCatalogEntry[]}
 */
export function getTexturesForWallSide(side) {
  return getTexturesForCategory('wall').filter((entry) => {
    const entrySide = inferWallSideFromTextureId(entry.id);
    return entrySide === side;
  });
}

/**
 * @param {string | null | undefined} textureId
 * @returns {FloorPlanTextureCatalogEntry | null}
 */
export function getTextureCatalogEntry(textureId) {
  if (!textureId) return null;
  return catalogById.get(textureId) ?? null;
}

/**
 * @returns {string[]}
 */
export function getAllTextureCatalogSources() {
  return FLOORPLAN_TEXTURE_CATALOG.map((entry) => entry.source);
}
